<template>
  <div id="app">
    <ControlGroup>
      <Dropdown
        v-if="!indicator"
        key="flowdd"
        :options="Object.keys(flows)"
        v-model="flow"
      />
      <Multiselect
        :options="
          regions.map((region) => ({
            label: region.country,
            value: region.stats,
          }))
        "
        default-option="Select regions to add/remove"
        :reset-on-invalid="false"
        v-model="selectedCountries"
      />
      <DataGetter
        v-if="!projections"
        :url="`${apiRoute}/list/country`"
        :params="{ flow }"
        @fetched="setCountries"
        :child-element-type="null"
        v-slot="{ data }"
      >
        <Multiselect
          v-if="data"
          :options="
            countries.map((country) => ({
              label: country.country,
              value: country.stats,
            }))
          "
          default-option="Select countries to add/remove"
          :reset-on-invalid="false"
          v-model="selectedCountries"
        />
      </DataGetter>
      <Dropdown
        v-if="projections"
        key="scenariodd"
        :options="[
          'Stated Policies Scenario',
          'WEO Net Zero Emissions by 2050 Scenario',
        ]"
        v-model="scenario"
      />
    </ControlGroup>
    <div class="container">
      <ChartLayout
        :title="`${flow}${projections ? ` in the ${scenario}` : ''}`"
        :units="units"
      >
        <DataGetter
          :url="`${apiRoute}/`"
          :params="{
            country: selectedCountries,
            scenario: projections ? [scenario, 'Historical'] : 'Historical',
            flow,
            type,
          }"
          v-slot="{ data }"
          child-element-type="span"
        >
          <DataSlicer
            :data="data"
            y-axis-dimension="countryName"
            x-axis-dimension="year"
            value-dimension="value"
            v-slot="{ slicedData }"
            :child-element-type="null"
          >
            <Chart
              :options="{
                ...chartOptions,
                series: (slicedData || []).map((series) => ({
                  ...series,
                  colorIndex: getSeriesIndex(series.name),
                  data: series.data.map((point, i) => ({
                    x: point[0],
                    y: point[1],
                    marker:
                      i === series.data.length - 1
                        ? { enabled: true }
                        : undefined,
                  })),
                })),
              }"
              :update-args="[true, true, true]"
            />
          </DataSlicer>
        </DataGetter>
        <template v-slot:sources v-if="notes">
          <span v-html="notes" />
        </template>
      </ChartLayout>
    </div>
  </div>
</template>

<script>
/* global Highcharts */
import { Chart } from "highcharts-vue";
import {
  DataGetter,
  DataSlicer,
  ControlGroup,
  Multiselect,
  Dropdown,
} from "@international-energy-agency/vue-ui";
import ChartOptions, {
  ChartLayout,
} from "@international-energy-agency/vue-ui/highcharts";
import notes from "./notes";
import unCountries from "./unCountries";
Highcharts.setOptions({
  lang: {
    numericSymbols: null,
  },
});
export default {
  name: "App",
  components: {
    DataGetter,
    DataSlicer,
    ChartLayout,
    Chart,
    ControlGroup,
    Multiselect,
    Dropdown,
  },
  props: {
    projections: {
      type: Boolean,
      default: false,
    },
    indicator: {
      type: String,
      default: null,
    },
    test: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const flows = {
      "Energy intensity measured in terms of primary energy and GDP":
        " MJ per 2017 USD PPP",
      "Modern renewable share in total final energy consumption": "%",
      "Share of population with access to clean cooking": "%",
      "Share of population with access to electricity": "%",
    };
    return {
      apiRoute: `https://api.${this.test ? "test.iea.org" : "iea.org"}/sdg`,
      multiselected: [],
      selectedCountries: ["WORLD"],
      scenario: "Stated Policies Scenario",
      flows,
      flow: this.indicator || Object.keys(flows)[0],
      countries: [],
      regions: [
        { country: "Asia Pacific", stats: "WEOASIAPAC" },
        { country: "Central and South America", stats: "WEOCSAM" },
        { country: "Eurasia", stats: "WEOEURASIA" },
        { country: "Europe", stats: "WEOEUR" },
        { country: "Middle East", stats: "WEOMIDEAST" },
        { country: "North Africa", stats: "WSRNAFR" },
        { country: "North America", stats: "WEONAM" },
        { country: "Sub-Saharan Africa", stats: "WSROAFR" },
      ],
    };
  },
  computed: {
    unsdCountries() {
      return this.selectedCountries.reduce((countries, country) => {
        if (unCountries.includes(country)) {
          const countryName = this.countries.find((c) => c.stats === country)
            .country;
          if (countryName) {
            countries.push(countryName);
          }
        }
        return countries;
      }, []);
    },
    units() {
      return this.flows[this.flow];
    },
    notes() {
      const { unsdCountries, flow } = this;
      const flowNotes = notes[this.flow] || null;
      if (!flowNotes && unsdCountries.length === 0) return null;
      const unsdCountryString =
        [
          "Energy intensity measured in terms of primary energy and GDP",
          "Modern renewable share in total final energy consumption",
        ].includes(flow) && unsdCountries.length > 0
          ? `Data for ${
              unsdCountries.length > 1
                ? `${unsdCountries.slice(0, -1).join(", ")} and `
                : ""
            }${
              unsdCountries[unsdCountries.length - 1]
            } provided by the United Nations Statistics Division. `
          : "";
      return `${unsdCountryString}${flowNotes}`;
    },
    type() {
      return this.projections ? undefined : "Historical";
    },
    chartOptions() {
      const { units, projections } = this;
      return ChartOptions.buildOptions(
        {
          yAxis: {
            max: units === "%" ? 100 : undefined,
          },

          xAxis: {
            plotLines: projections
              ? [
                  {
                    value: 2021,
                    label: {
                      text: "Projection",
                    },
                  },
                ]
              : undefined,
          },
          tooltip: {
            valueSuffix: units,
          },
        },
        "line"
      );
    },
  },
  methods: {
    setCountries(countries) {
      this.countries = countries.filter(
        (country) => country.country !== "Chinese Taipei"
      );
    },
    getSeriesIndex(regionOrCountry) {
      if (regionOrCountry === "World") return 99;
      return null;
      /*
      const index = [
        ...this.countries,
        ...this.regions
      ].findIndex(item => item.country === regionOrCountry);
      return index > -1
        ? index % 10
        : undefined;
        */
    },
    setUnits(units) {
      this.units = units;
    },
    round(num) {
      return num < 1 ? num.toFixed(2) : num.toFixed(0);
    },
  },
};
</script>

<style>
.highcharts-sankey-series .highcharts-link {
  fill-opacity: 0.75;
}
</style>
